import { AppConfig } from "../AppConfig";
import { getAGEToken } from "./authenticate";

export type LogLevel = "log" | "warn" | "error" | "info" | "debug";

export interface LogEntry {
    timestamp: number;
    level: LogLevel;
    messages: string[];
}

const LOG_RETENTION_MS = 5 * 60 * 1000; // 5 minutes
const MAX_ENTRIES = 500;

const logBuffer: LogEntry[] = [];

const originalConsole: Record<LogLevel, (...args: unknown[]) => void> = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
};

const SENSITIVE_KEY_NAMES = [
    "access_token",
    "refresh_token",
    "id_token",
    "token",
    "api_key",
    "apikey",
    "password",
    "secret",
];

const SENSITIVE_KEYS_ALT = SENSITIVE_KEY_NAMES.join("|");
const QUERY_PARAM_PATTERN = new RegExp(`\\b(${SENSITIVE_KEYS_ALT})=([^&\\s"'<>#]+)`, "gi");
const JSON_FIELD_PATTERN = new RegExp(`("(?:${SENSITIVE_KEYS_ALT})"\\s*:\\s*)"[^"]+"`, "gi");
const BEARER_PATTERN = /\b(Bearer\s+)[A-Za-z0-9._\-]+/gi;

export function redactSensitive(input: string): string {
    return input
        .replace(QUERY_PARAM_PATTERN, "$1=REDACTED")
        .replace(JSON_FIELD_PATTERN, '$1"REDACTED"')
        .replace(BEARER_PATTERN, "$1REDACTED");
}

function serialize(arg: unknown): string {
    try {
        if (arg instanceof Error) return redactSensitive(`${arg.name}: ${arg.message}\n${arg.stack ?? ""}`);
        if (typeof arg === "object" && arg !== null) return redactSensitive(JSON.stringify(arg, null, 2));
        return redactSensitive(String(arg));
    } catch {
        return "[unserializable]";
    }
}

function pruneOldEntries() {
    const cutoff = Date.now() - LOG_RETENTION_MS;
    while (logBuffer.length > 0 && logBuffer[0].timestamp < cutoff) {
        logBuffer.shift();
    }
    while (logBuffer.length > MAX_ENTRIES) {
        logBuffer.shift();
    }
}

function isSuppressed(args: unknown[]): boolean {
    if (args.length === 0) return false;
    const first = typeof args[0] === "string" ? args[0] : "";
    const second = typeof args[1] === "string" ? args[1] : "";
    if (
        first.startsWith("[esri.views.2d.layers.features.FeatureSourceEventLog]") &&
        second.startsWith("Failed to load tile")
    )
        return true; // Suppress noisy SDK messages
    return false;
}

function intercept(level: LogLevel) {
    console[level] = (...args: unknown[]) => {
        if (isSuppressed(args)) return;
        originalConsole[level](...args);
        pruneOldEntries();
        logBuffer.push({ timestamp: Date.now(), level, messages: args.map(serialize) });
    };
}

export function initConsoleCapture() {
    // this is configured in env.js
    // otherwise all console outputs' source file is this file - you prob do not want that when debugging
    if (window.BYPASS_CONSOLE_CAPTURE) return;

    (["log", "warn", "error", "info", "debug"] as const).forEach(intercept);

    window.addEventListener("error", (event) => {
        pruneOldEntries();
        logBuffer.push({
            timestamp: Date.now(),
            level: "error",
            messages: [
                redactSensitive(`Uncaught: ${event.message} (${event.filename}:${event.lineno}:${event.colno})`),
            ],
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        pruneOldEntries();
        logBuffer.push({
            timestamp: Date.now(),
            level: "error",
            messages: [`Unhandled Promise Rejection: ${serialize(event.reason)}`],
        });
    });
}

export function getRecentLogs(): LogEntry[] {
    pruneOldEntries();
    return [...logBuffer];
}

export function formatLogsAsText(username: string): string {
    const entries = getRecentLogs();
    if (entries.length === 0) return `Ingen nye loggmeldinger i de siste ${LOG_RETENTION_MS / 60000} minuttene.`;

    const header = [
        `Feilrapport generert: ${new Date().toISOString()}`,
        `URL: ${redactSensitive(window.location.href)}`,
        `Brukernavn: ${username}`,
        `Antall loggmeldinger: ${entries.length}`,
        "─".repeat(50),
    ].join("\n");

    const body = entries
        .map((entry) => {
            const time = new Date(entry.timestamp).toLocaleTimeString("nb-NO", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                fractionalSecondDigits: 3,
            });
            return `[${time}] [${entry.level.toUpperCase().padEnd(5)}] ${entry.messages.join(" ")}`;
        })
        .join("\n");

    return `${header}\n${body}`;
}

export async function sendLogsToApi(
    username: string,
    referenceId: string,
    screenshotDataUrl?: string,
    userInfo?: string
): Promise<{ sent: number; failed: number }> {
    const entries = getRecentLogs();
    const url = redactSensitive(window.location.href);
    const apiUrl = `${AppConfig.LogApi.BaseApiUrl}logs`;
    const token = await getAGEToken();

    const logEntries = entries.map((entry) => ({
        timestamp: new Date(entry.timestamp).toISOString(),
        logLevel: entry.level,
        message: entry.messages.join(" "),
        context: "",
        username,
        referenceId: referenceId,
    }));

    logEntries.push({
        timestamp: new Date().toISOString(),
        logLevel: "info",
        message: url,
        context: "url",
        username,
        referenceId: referenceId,
    });

    if (userInfo) {
        logEntries.push({
            timestamp: new Date().toISOString(),
            logLevel: "info",
            message: userInfo,
            context: "userinfo",
            username,
            referenceId: referenceId,
        });
    }

    if (screenshotDataUrl) {
        // dataUrl format: "data:<mimeType>;base64,<data>"
        const [prefix, base64Data] = screenshotDataUrl.split(",");
        logEntries.push({
            timestamp: new Date().toISOString(),
            logLevel: "info",
            message: base64Data,
            context: prefix,
            username,
            referenceId: referenceId,
        });
    }

    const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(logEntries),
    });

    if (!response.ok) {
        return { sent: 0, failed: entries.length };
    }

    // remove all from logBuffer that have been reported successfully
    const sentTimestamps = new Set(entries.map((e) => e.timestamp));
    for (let i = logBuffer.length - 1; i >= 0; i--) {
        if (sentTimestamps.has(logBuffer[i].timestamp)) {
            logBuffer.splice(i, 1);
        }
    }

    return { sent: entries.length, failed: 0 };
}
